import {
  Box,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnlineTourCard } from '../components/OnlineTourCard';
import OnlineTourDetailHotels from '../components/OnlineTourDetailHotels';
import OnlineTourDetailPresentations from '../components/OnlineTourDetailPresentations';
import OnlineTourDetailPrices from '../components/OnlineTourDetailPrices';
import OnlineTourDetailTransportation from '../components/OnlineTourDetailTransportation';
import {
  IOnlineTourSearchResult,
  OnlineTourDetailTabTypes,
} from '../utils/types';
import '../App.css';
import OnlineTourDetailTransportationMobile from '../components/OnlineTourDetailTransportationMobile';
import { sendToNative } from '../utils';

type OnlineTourDetailPageProps = {
  onlineSearchResult: IOnlineTourSearchResult[];
  tourId: number;
};

export default function OnlineTourDetailPrintOutContainer({
  onlineSearchResult,
  tourId,
}: OnlineTourDetailPageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isLoaded, setIsLoaded] = useState(false);
  useState<OnlineTourDetailTabTypes>('presentation');

  const filteredResults = onlineSearchResult.filter(
    (result) => result.id === tourId,
  );
  const handleTabHide = () => {
    return 0;
  };

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const timer = setTimeout(() => {
        if (window.Android) {
          sendToNative('print', null);
        } else {
          window.print();
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isLoaded]);

  return (
    <Box sx={{ maxWidth: '1400px', margin: 'auto' }}>
      {filteredResults?.map((r: IOnlineTourSearchResult, i: number) => (
        <Box key={i} className="print-section">
          <Box className="tab-section">
            <OnlineTourCard
              disableActions
              tourId={r.id}
              coverImg={r.images.length > 0 ? r.images[0].path : null}
              flightCompanyInfo={
                r?.flightCompanyInfo && r?.flightCompanyInfo[0]
              }
              tourName={r.name}
              tourCode={r.code}
              start={{
                departureCity: r.departureDepartureCity,
                departureAirport: r.departureDeparture,
                arrivalCity: r.departureArrivalCity,
                arrivalAirport: r.departureArrival,
              }}
              end={{
                departureCity: r.arrivalDepartureCity,
                departureAirport: r.arrivalDeparture,
                arrivalCity: r.arrivalArrivalCity,
                arrivalAirport: r.arrivalArrival,
              }}
              tourStartDate={r.departureDate}
              tourFinishDate={r.arrivalDate}
              night={r.dayCount - 1}
              day={r.dayCount}
              tourPrice={r.adultPrice}
              currencyCode={r.currencyCode}
              remainingTime={r.remainingDay}
              remainingSeat={r.availableCapacity}
              key={i}
            />
          </Box>
        </Box>
      ))}

      <Box sx={{ mt: 2, minHeight: '100vh' }}>
        <Box className="print-section">
          <Box
            className="tab-section"
            sx={{
              border: '1px groove',
              borderRadius: '10px',
              margin: '20px 0px',
              padding: 2,
              mt: 2,
            }}
          >
            <Typography variant="h5">
              {t('onlineTourDetail.presentation')}
            </Typography>
            <Divider sx={{ border: '1px groove', margin: '20px 0px' }} />
            <OnlineTourDetailPresentations
              tourId={tourId}
              onEmptyDetails={handleTabHide}
            />
          </Box>
          <Box
            className="tab-section"
            sx={{
              border: '1px groove',
              borderRadius: '10px',
              margin: '20px 0px',
              padding: 2,
              mt: 2,
            }}
          >
            <Typography variant="h5">{t('onlineTourDetail.prices')}</Typography>
            <Divider sx={{ border: '1px groove', margin: '20px 0px' }} />
            <OnlineTourDetailPrices tourId={tourId} />
          </Box>
          <Box
            className="tab-section"
            sx={{
              border: '1px groove',
              borderRadius: '10px',
              margin: '20px 0px',
              padding: 2,
              mt: 2,
            }}
          >
            <Typography variant="h5">{t('onlineTourDetail.hotels')}</Typography>
            <Divider sx={{ border: '1px groove', margin: '20px 0px' }} />
            <OnlineTourDetailHotels tourId={tourId} />
          </Box>
          <Box
            className="tab-section"
            sx={{
              border: '1px groove',
              borderRadius: '10px',
              margin: '20px 0px',
              padding: 2,
              mt: 2,
            }}
          >
            <Typography variant="h5">
              {t('onlineTourDetail.transportation')}
            </Typography>
            <Divider sx={{ border: '1px groove', margin: '20px 0px' }} />
            {isMobile ? (
              <OnlineTourDetailTransportationMobile tourId={tourId} />
            ) : (
              <OnlineTourDetailTransportation tourId={tourId} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
